/* eslint-disable camelcase */
import React, {
  useEffect,
  useCallback,
} from 'react';
import {
  Route,
  Routes,
  Navigate,
  useParams,
  useLocation,
  useNavigate,
} from 'react-router-dom';
import {
  shallowEqual,
  useDispatch,
  useSelector,
} from 'react-redux';
import {
  getStatus,
} from 'redux-resource';

import {
  coreActions,
  coreSelectors,
} from '@gymcloud/core';

import {
  getUiState,
} from 'web-selectors';
import {
  uiActions,
} from 'web-actions';
import {
  getBasePath,
} from 'web-utils';

import {
  Button,
  Spinner,
  NotFound,
  Breadcrumbs,
  FillEntity,
  ContentWrapper,
} from 'web-components';

import {
  Flex,
} from 'web-styled';

type Props = {
  requestType?: string;
  resourceType: 'exercises' | 'workoutExercises' | 'personalExercises';
};

const ExercisesPage = ({
  requestType,
  resourceType,
}: Props) => {
  const params = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const id = Number(params.id);

  const mapState = useCallback(state => ({
    profile: coreSelectors.getProfile(state),
    exercise: coreSelectors.getResourceById(
      resourceType,
      id,
    )(state),
    readStatus: getStatus(
      state,
      [
        `${resourceType}.meta[${id}].readStatus`,
        `${resourceType}.requests.getEntity.status`,
      ],
      true,
    ),
    updateStatus: getStatus(
      state,
      [
        `${resourceType}.meta[${id}].updateStatus`,
      ],
    ),
    breadcrumbs: coreSelectors.getBreadcrumbs(
      resourceType,
      id,
    )(state),
    ...getUiState([
      'openedTooltips',
    ])(state),
  }), [id]);

  const {
    profile,
    exercise,
    readStatus,
    updateStatus,
    breadcrumbs,
    openedTooltips,
  } = useSelector(mapState, shallowEqual);
  const userSteps = profile.user_settings.tutorial_steps;

  useEffect(() => {
    dispatch(
      coreActions.requestFullEntity({
        id,
        requestType,
        resourceType,
      }),
    );
  }, [id]);

  const basePath = getBasePath(resourceType);
  const isEdit = location.pathname && location.pathname.indexOf('edit') !== -1;

  const handleSave = () => {
    const btn = document.getElementById('formSubmitButton');
    if (btn) {
      btn.click();
    }
  };

  return (
    <ContentWrapper
      header={(
        <Flex fillSpace align="center" justify="space-between">
          <Breadcrumbs
            items={breadcrumbs}
            basePath={basePath}
            backPath={exercise?.is_public ? undefined : '/exercises_folder'}
            entityFolderId={exercise?.is_public ? undefined : exercise?.folder_id}
          />
          {(profile.is_pro && resourceType === 'exercises') && (
            <Flex align="center" spacing={10}>
              <Button
                ghost
                appearance="primary"
                isLoading={updateStatus.pending}
                onClick={() => {
                  if (isEdit) {
                    handleSave();
                  } else {
                    navigate(`${basePath}/${id}/edit`);
                  }
                }}
              >
                {isEdit ? 'Save' : 'Edit'}
              </Button>
              <Button
                ghost
                appearance="danger"
                onClick={() => {
                  if (isEdit) {
                    navigate(`${basePath}/${id}/overview`);
                    return;
                  }

                  const onSuccess = () => {
                    if (exercise.workout_id) {
                      navigate(`/workout_templates/${exercise.workout_id}`);
                    } else if (exercise.folder_id) {
                      navigate(`${basePath}_folder/${exercise.folder_id}`);
                    } else {
                      navigate(-1);
                    }
                  };
                  const onConfirmAction = coreActions.requestDeleteEntity({
                    id,
                    resourceType,
                  });

                  dispatch(
                    uiActions.confirmModal({
                      modal: {
                        title: 'Delete Exercise?',
                        bodyText: `Are you sure you want to delete <b>${exercise?.name}</b>?`,
                        confirmText: 'Delete',
                      },
                      onSuccess,
                      onConfirmAction,
                    }),
                  );
                }}
              >
                {isEdit ? 'Cancel' : 'Delete'}
              </Button>
            </Flex>
          )}
        </Flex>
      )}
    >
      {readStatus.succeeded && (
        <Routes>
          <Route
            index
            element={(
              <Navigate to="overview" replace />
            )}
          />
          <Route
            path="overview"
            element={(
              <FillEntity
                readOnly
                isPro={profile.is_pro}
                entity={exercise}
                resourceType={resourceType}
              />
            )}
          />
          {resourceType === 'exercises' && (
            <Route
              path="edit"
              element={(
                <FillEntity
                  entity={exercise}
                  resourceType={resourceType}
                  openedTooltips={openedTooltips}
                  onSubmit={() => {
                    if (openedTooltips.length) {
                      dispatch(
                        uiActions.setUiState({
                          openedTooltips: [],
                        }),
                      );
                    }
                    if (!userSteps.includes('add_exercise')) {
                      dispatch(
                        coreActions.requestMarkTutotialStepsComplite({
                          steps: ['add_exercise'],
                        }),
                      );
                    }
                  }}
                />
              )}
            />
          )}
        </Routes>
      )}

      {readStatus.pending && (
        <Spinner message="Loading Exercise..." />
      )}

      {readStatus.failed && (
        <NotFound title={`Exercise Not Found (${id})`} />
      )}
    </ContentWrapper>
  );
};

ExercisesPage.defaultProps = {
  requestType: undefined,
};

export default ExercisesPage;
